import React, { useRef, useState, useEffect } from "react";
import { Image as ImageIcon, Video, Music, FileText, X, Globe, Users, Loader2, Play, Camera, Mic, Square, Trash2, Circle } from "lucide-react";
import api from "@/lib/api";
import { uploadFileChunked } from "@/lib/uploads";
import { compressImage, videoPoster } from "@/lib/imageCompress";
import { encodeImageMeta } from "@/lib/blur";
import { getInitials, avatarColor } from "@/lib/avatar";
import VideoRecorder from "@/components/social/VideoRecorder";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const DRAFT_KEY = "rc_post_draft";
const loadDraft = () => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}"); } catch { return {}; } };
const fmtDur = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function PostComposer({ onPosted }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const fr = (typeof localStorage !== "undefined" && localStorage.getItem("rc_lang") === "fr");
  const [text, setText] = useState(() => loadDraft().text || "");
  const [visibility, setVisibility] = useState(() => loadDraft().visibility || "public");
  const [media, setMedia] = useState([]); // {url,type,blur_hash,w,h,name,preview}
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState(null); // upload progress 0..100 (null = idle)
  const [showRecorder, setShowRecorder] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const imgRef = useRef(null);
  const camRef = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const docRef = useRef(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const cancelRecRef = useRef(false);
  const audioCtxRef = useRef(null);
  const waveRafRef = useRef(null);
  const [levels, setLevels] = useState([]); // live waveform bar heights (0..1)

  // Auto-save / restore a half-written post as a draft.
  useEffect(() => {
    if (text.trim()) localStorage.setItem(DRAFT_KEY, JSON.stringify({ text, visibility }));
    else localStorage.removeItem(DRAFT_KEY);
  }, [text, visibility]);

  const stopStream = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((tr) => tr.stop()); streamRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (waveRafRef.current) { cancelAnimationFrame(waveRafRef.current); waveRafRef.current = null; }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} audioCtxRef.current = null; }
    setLevels([]);
  };

  const startWaveform = (stream) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const loop = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const level = Math.min(1, Math.sqrt(sum / buf.length) * 3.2);
        setLevels((prev) => [...prev, level].slice(-48));
        waveRafRef.current = requestAnimationFrame(loop);
      };
      loop();
    } catch {}
  };

  const startRec = async () => {
    if (recording) return;
    if (!navigator.mediaDevices || typeof MediaRecorder === "undefined") {
      toast.error(fr ? "Enregistrement audio non pris en charge" : "Voice recording isn't supported here");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      startWaveform(stream);
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      cancelRecRef.current = false;
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const type = mr.mimeType || "audio/webm";
        stopStream();
        if (cancelRecRef.current) return;
        const blob = new Blob(chunksRef.current, { type });
        if (!blob.size) return;
        const ext = type.includes("mp4") || type.includes("aac") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type });
        try {
          setBusy(true);
          const { url } = await uploadFileChunked(file, (pct) => setUploadPct(pct), "social");
          setMedia((m) => [...m, { url, type: "audio", name: fr ? "Message vocal" : "Voice message" }]);
        } catch { toast.error(t("s_could_not_upload_media")); } finally { setBusy(false); setUploadPct(null); }
      };
      mr.start();
      mrRef.current = mr;
      setRecording(true);
      setRecSecs(0);
      timerRef.current = setInterval(() => setRecSecs((s) => {
        if (s >= 299) { try { mr.stop(); } catch {} setRecording(false); } // 5-min safety cap
        return s + 1;
      }), 1000);
    } catch {
      toast.error(fr ? "Micro inaccessible. Autorisez l'accès." : "Couldn't access the mic. Allow access.");
      stopStream();
    }
  };

  const stopRec = () => {
    if (!recording) return;
    setRecording(false);
    try { mrRef.current && mrRef.current.stop(); } catch {}
  };
  const cancelRec = () => {
    if (!recording) return;
    cancelRecRef.current = true;
    setRecording(false);
    try { mrRef.current && mrRef.current.stop(); } catch {}
  };

  useEffect(() => () => { stopStream(); }, []);

  const clearDraft = () => { setText(""); setMedia([]); setVisibility("public"); localStorage.removeItem(DRAFT_KEY); };

  const pickImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files.slice(0, 5)) {
      try {
        setBusy(true); setUploadPct(0);
        const preview = URL.createObjectURL(f);
        const toSend = await compressImage(f, { maxDim: 1600, quality: 0.7 }); // always shrink for faster posting
        const meta = await encodeImageMeta(toSend).catch(() => ({}));
        const { url } = await uploadFileChunked(toSend, (pct) => setUploadPct(pct), "social");
        setMedia((m) => [...m, { url, type: "image", blur_hash: meta.blur_hash, w: meta.media_w, h: meta.media_h, preview }]);
      } catch { toast.error(t("s_could_not_upload")); } finally { setBusy(false); setUploadPct(null); }
    }
  };

  const pickFile = (kind) => async (e) => {
    const f = (e.target.files || [])[0];
    e.target.value = "";
    if (!f) return;
    try {
      setBusy(true); setUploadPct(0);
      const preview = kind === "video" ? URL.createObjectURL(f) : null;
      const { url } = await uploadFileChunked(f, (pct) => setUploadPct(pct), "social");
      const item = { url, type: kind, name: f.name, preview };
      if (kind === "video") {
        // Auto-generate a poster (first frame) so the post loads instantly
        try {
          const posterFile = await videoPoster(f);
          if (posterFile) {
            const meta = await encodeImageMeta(posterFile).catch(() => ({}));
            const { url: posterUrl } = await uploadFileChunked(posterFile, null, "social");
            item.poster = posterUrl;
            item.blur_hash = meta.blur_hash;
            item.w = meta.media_w;
            item.h = meta.media_h;
          }
        } catch {}
      }
      setMedia((m) => [...m, item]);
    } catch { toast.error(t("s_could_not_upload_media")); } finally { setBusy(false); setUploadPct(null); }
  };

  // Recorded in-app (already ~240p). Preview/retake happens inside VideoRecorder.
  const addRecordedVideo = async (file, posterFile) => {
    try {
      setBusy(true); setUploadPct(0);
      const preview = URL.createObjectURL(file);
      const { url } = await uploadFileChunked(file, (pct) => setUploadPct(pct), "social");
      const item = { url, type: "video", name: file.name, preview };
      if (posterFile) {
        try {
          const meta = await encodeImageMeta(posterFile).catch(() => ({}));
          const { url: posterUrl } = await uploadFileChunked(posterFile, null, "social");
          item.poster = posterUrl; item.blur_hash = meta.blur_hash; item.w = meta.media_w; item.h = meta.media_h;
        } catch {}
      }
      setMedia((m) => [...m, item]);
    } catch { toast.error(t("s_could_not_upload_media")); } finally { setBusy(false); setUploadPct(null); }
  };

  const removeMedia = (i) => setMedia((m) => m.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!text.trim() && media.length === 0) return;
    try {
      setBusy(true);
      const { data } = await api.post("/social/posts", {
        text: text.trim(), visibility,
        media: media.map(({ url, type, blur_hash, w, h, name, poster }) => ({ url, type, blur_hash, w, h, name, poster })),
      });
      setText(""); setMedia([]); setVisibility("public");
      localStorage.removeItem(DRAFT_KEY);
      onPosted && onPosted(data);
      toast.success(t("s_posted"));
    } catch { toast.error(t("s_could_not_post")); } finally { setBusy(false); }
  };

  const AttachBtn = ({ onClick, icon: Icon, label, testid }) => (
    <button onClick={onClick} data-testid={testid} title={label}
      className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs text-[var(--rc-gold)] hover:bg-[var(--rc-bg)]">
      <Icon size={18} /> <span className="hidden xs:inline">{label}</span>
    </button>
  );

  return (
    <div className="rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-surface)] p-4" data-testid="post-composer">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-full grid place-items-center text-[var(--rc-gold)] font-semibold border-2 border-[var(--rc-gold)] overflow-hidden shrink-0" style={{ backgroundColor: user.avatar ? "var(--rc-bg)" : avatarColor(user.name) }}>
          {user.avatar ? <img src={user.avatar} alt="" className="h-full w-full object-cover" /> : getInitials(user.name)}
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={t("s_whats_mind")} data-testid="post-text-input"
          className="flex-1 min-w-0 resize-none bg-transparent text-base focus:outline-none placeholder:text-[var(--rc-muted)] pt-2" />
      </div>

      {uploadPct !== null && (
        <div className="mt-3 flex items-center gap-2" data-testid="post-upload-progress">
          <div className="flex-1 h-1.5 rounded-full bg-[var(--rc-bg)] overflow-hidden">
            <div className="h-full bg-[var(--rc-gold)] transition-all duration-200" style={{ width: `${uploadPct}%` }} data-testid="post-upload-progress-fill" />
          </div>
          <span className="text-[11px] tabular-nums text-[var(--rc-muted)] shrink-0">{uploadPct}%</span>
        </div>
      )}

      {media.length > 0 && (
        <div className="mt-3 space-y-2">
          {media.some((m) => m.type === "image" || m.type === "video") && (
            <div className="grid grid-cols-4 gap-2">
              {media.map((m, i) => (m.type === "image" || m.type === "video") ? (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-[var(--rc-bg)]">
                  {m.type === "video"
                    ? <><video src={m.preview} muted className="h-full w-full object-cover" /><span className="absolute inset-0 grid place-items-center text-white/90"><Play size={20} /></span></>
                    : <img src={m.preview} alt="" className="h-full w-full object-cover" />}
                  <button onClick={() => removeMedia(i)} data-testid={`post-media-remove-${i}`} className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-black/60 text-white"><X size={13} /></button>
                </div>
              ) : null)}
            </div>
          )}
          {media.map((m, i) => (m.type === "audio" || m.type === "document") ? (
            <div key={i} className="flex items-center gap-2 rounded-xl bg-[var(--rc-bg)] border border-[var(--rc-border)] px-3 py-2">
              {m.type === "audio" ? <Music size={18} className="text-[var(--rc-gold)] shrink-0" /> : <FileText size={18} className="text-[var(--rc-gold)] shrink-0" />}
              <span className="flex-1 min-w-0 truncate text-sm">{m.name}</span>
              <button onClick={() => removeMedia(i)} data-testid={`post-media-remove-${i}`} className="h-6 w-6 grid place-items-center rounded-full bg-black/40 text-white shrink-0"><X size={13} /></button>
            </div>
          ) : null)}
        </div>
      )}

      {recording && (
        <div className="mt-3 flex items-center gap-3 rounded-xl border border-red-500/40 bg-red-500/5 px-3 py-2.5" data-testid="post-recording-bar">
          <span className="h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse shrink-0" />
          <div className="flex-1 min-w-0 h-8 flex items-center gap-[2px] overflow-hidden" data-testid="post-waveform">
            {levels.length === 0
              ? <span className="text-xs text-[var(--rc-muted)]">{fr ? "Enregistrement…" : "Recording…"}</span>
              : levels.map((lv, i) => (
                <span key={i} className="w-[3px] shrink-0 rounded-full bg-[var(--rc-gold)]"
                  style={{ height: `${Math.max(8, lv * 100)}%`, opacity: 0.5 + lv * 0.5 }} />
              ))}
          </div>
          <span className="text-sm tabular-nums text-[var(--rc-muted)] shrink-0">{fmtDur(recSecs)}</span>
          <button onClick={cancelRec} data-testid="post-rec-cancel" title={fr ? "Annuler" : "Cancel"}
            className="h-8 w-8 grid place-items-center rounded-full text-[var(--rc-muted)] hover:bg-[var(--rc-bg)] shrink-0"><Trash2 size={16} /></button>
          <button onClick={stopRec} data-testid="post-rec-stop"
            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--rc-gold)] text-black font-semibold px-3 py-1.5 text-sm shrink-0"><Square size={13} fill="black" /> {fr ? "Terminer" : "Stop"}</button>
        </div>
      )}

      <div className="mt-3 flex items-center gap-1 flex-wrap">
        <input ref={imgRef} type="file" accept="image/*" multiple hidden onChange={pickImages} data-testid="post-image-input" />
        <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={pickImages} data-testid="post-camera-input" />
        <input ref={videoRef} type="file" accept="video/*" hidden onChange={pickFile("video")} data-testid="post-video-input" />
        <input ref={audioRef} type="file" accept="audio/*" hidden onChange={pickFile("audio")} data-testid="post-audio-input" />
        <input ref={docRef} type="file" hidden onChange={pickFile("document")} data-testid="post-doc-input" />
        <AttachBtn onClick={() => camRef.current?.click()} icon={Camera} label={t("s_take_photo")} testid="post-take-photo" />
        <AttachBtn onClick={() => imgRef.current?.click()} icon={ImageIcon} label={t("s_add_photo")} testid="post-add-image" />
        <AttachBtn onClick={() => videoRef.current?.click()} icon={Video} label={t("s_add_video")} testid="post-add-video" />
        <AttachBtn onClick={() => setShowRecorder(true)} icon={Circle} label={fr ? "Filmer" : "Record"} testid="post-record-video" />
        <AttachBtn onClick={() => audioRef.current?.click()} icon={Music} label={t("s_add_audio")} testid="post-add-audio" />
        <button onClick={recording ? stopRec : startRec} data-testid="post-record-voice" title={fr ? "Enregistrer un message vocal" : "Record voice"}
          className={`flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs hover:bg-[var(--rc-bg)] ${recording ? "text-red-400" : "text-[var(--rc-gold)]"}`}>
          <Mic size={18} /> <span className="hidden xs:inline">{fr ? "Vocal" : "Voice"}</span>
        </button>
        <AttachBtn onClick={() => docRef.current?.click()} icon={FileText} label={t("s_add_document")} testid="post-add-doc" />
      </div>

      <div className="mt-3 flex items-center gap-2 border-t border-[var(--rc-border)] pt-3">
        <button onClick={() => setVisibility((v) => (v === "public" ? "friends" : "public"))} data-testid="post-visibility"
          className="flex items-center gap-1.5 rounded-full border border-[var(--rc-border)] px-3 py-1.5 text-xs text-[var(--rc-muted)]">
          {visibility === "public" ? <><Globe size={14} /> {t("s_public")}</> : <><Users size={14} /> {t("s_friends")}</>}
        </button>
        {text.trim() && (
          <button onClick={clearDraft} data-testid="post-draft-clear"
            className="hidden sm:inline-flex items-center gap-1 text-[11px] text-[var(--rc-muted)] hover:text-[var(--rc-text)]">
            {fr ? "Brouillon enregistré · Effacer" : "Draft saved · Discard"}
          </button>
        )}
        <div className="flex-1" />
        <button onClick={submit} disabled={busy || (!text.trim() && media.length === 0)} data-testid="post-submit"
          className="rounded-full bg-[var(--rc-gold)] text-black font-semibold px-5 py-2 text-sm disabled:opacity-40 flex items-center gap-2">
          {busy && <Loader2 size={15} className="animate-spin" />} {t("s_post")}
        </button>
      </div>

      {showRecorder && (
        <VideoRecorder onClose={() => setShowRecorder(false)}
          onUse={(file, posterFile) => { setShowRecorder(false); addRecordedVideo(file, posterFile); }} />
      )}
    </div>
  );
}
